import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Button,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/common';
import { useAuthStore } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import { User } from '../../models';

interface ProfileFormData {
  name: string;
  email: string;
  department: string;
  phone: string;
}

interface WeDMSFormData {
  wedms_username: string;
  wedms_password: string;
}

const ProfilePage: React.FC = () => {
  const { user, fetchUser } = useAuthStore();
  const queryClient = useQueryClient();
  const [wedmsDialogOpen, setWedmsDialogOpen] = useState(false);
  const [wedmsError, setWedmsError] = useState<string | null>(null);

  const { control, handleSubmit, reset, formState: { isDirty } } = useForm<ProfileFormData>({
    defaultValues: {
      name: '',
      email: '',
      department: '',
      phone: '',
    },
  });

  const {
    control: wedmsControl,
    handleSubmit: handleWedmsSubmit,
    reset: resetWedmsForm,
  } = useForm<WeDMSFormData>({
    defaultValues: {
      wedms_username: '',
      wedms_password: '',
    },
  });

  // 사용자 정보 로드
  useEffect(() => {
    if (user) {
      reset({
        name: user.name || '',
        email: user.email || '',
        department: user.department || '',
        phone: user.phone || '',
      });
    }
  }, [user, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<User>) => authService.updateProfile(data),
    onSuccess: () => {
      fetchUser();
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  // WeDMS 연동 후 자동 처리 상태
  const [processingPending, setProcessingPending] = useState(false);
  const [pendingResult, setPendingResult] = useState<{
    message: string;
    recordings: { started: number; failed: number };
    templates: { started: number; failed: number };
  } | null>(null);

  // WeDMS 연동
  const connectWedmsMutation = useMutation({
    mutationFn: (data: WeDMSFormData) => authService.connectWeDMS(data.wedms_username, data.wedms_password),
    onSuccess: async () => {
      fetchUser();
      setWedmsDialogOpen(false);
      resetWedmsForm();
      setWedmsError(null);

      // WeDMS 연동 성공 후 대기 중인 항목 자동 처리
      setProcessingPending(true);
      try {
        const result = await authService.processPendingWeDMS();
        const totalStarted = result.recordings.started + result.templates.started;
        if (totalStarted > 0) {
          setPendingResult({
            message: result.message,
            recordings: result.recordings,
            templates: result.templates,
          });
        }
      } catch (err) {
        console.error('Failed to process pending items:', err);
      } finally {
        setProcessingPending(false);
      }
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      setWedmsError(error.response?.data?.message || 'WeDMS 연동에 실패했습니다.');
    },
  });

  const disconnectWedmsMutation = useMutation({
    mutationFn: () => authService.disconnectWeDMS(),
    onSuccess: () => {
      fetchUser();
    },
  });

  const onSubmit = async (data: ProfileFormData) => {
    try {
      await updateMutation.mutateAsync(data);
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  const onWedmsSubmit = async (data: WeDMSFormData) => {
    setWedmsError(null);
    try {
      await connectWedmsMutation.mutateAsync(data);
    } catch (error) {
      console.error('Failed to connect WeDMS:', error);
    }
  };

  const handleOpenWedmsDialog = () => {
    setWedmsError(null);
    resetWedmsForm();
    setWedmsDialogOpen(true);
  };

  const handleDisconnectWedms = async () => {
    if (window.confirm('WeDMS 연동을 해제하시겠습니까?')) {
      await disconnectWedmsMutation.mutateAsync();
    }
  };

  return (
    <Box>
      <PageHeader
        title="프로필 관리"
        breadcrumbs={[{ label: '프로필' }]}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                상담자 정보
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                이 정보는 상담 기록지 작성 시 상담자 정보로 자동 입력됩니다.
              </Typography>

              {updateMutation.isSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  프로필이 성공적으로 업데이트되었습니다.
                </Alert>
              )}

              {updateMutation.isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  프로필 업데이트에 실패했습니다.
                </Alert>
              )}

              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: '이름을 입력하세요.' }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="이름 (상담자명)"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || '상담 기록지의 상담자 필드에 사용됩니다'}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="department"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="부서/소속"
                          helperText="예: 방문요양팀, 사회복지과"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: '올바른 이메일 형식이 아닙니다.',
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="email"
                          label="이메일"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="연락처"
                          placeholder="010-0000-0000"
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={!isDirty || updateMutation.isPending}
                  >
                    {updateMutation.isPending ? '저장 중...' : '저장'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => reset()}
                    disabled={!isDirty}
                  >
                    취소
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                계정 정보
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    사용자 ID
                  </Typography>
                  <Typography variant="body1">{user?.username}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    가입일
                  </Typography>
                  <Typography variant="body1">
                    {user?.date_joined
                      ? new Date(user.date_joined).toLocaleDateString('ko-KR')
                      : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    마지막 로그인
                  </Typography>
                  <Typography variant="body1">
                    {user?.last_login
                      ? new Date(user.last_login).toLocaleString('ko-KR')
                      : '-'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* WeDMS 연동 카드 */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <StorageIcon color="primary" />
                <Typography variant="h6">WeDMS 연동</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                WeDMS(문서관리시스템)와 연동하여 문서를 저장하고 관리할 수 있습니다.
              </Typography>

              {/* 자동 처리 진행 중 표시 */}
              {processingPending && (
                <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={16} />}>
                  대기 중인 녹음 파일과 템플릿을 WeDMS에 업로드하고 있습니다...
                </Alert>
              )}

              {/* 자동 처리 결과 표시 */}
              {pendingResult && (
                <Alert
                  severity="success"
                  sx={{ mb: 2 }}
                  onClose={() => setPendingResult(null)}
                >
                  {pendingResult.message}
                  {(pendingResult.recordings.started > 0 || pendingResult.templates.started > 0) && (
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                      녹음 파일 {pendingResult.recordings.started}개, 템플릿 {pendingResult.templates.started}개 처리 시작
                    </Typography>
                  )}
                </Alert>
              )}

              {user?.has_wedms_token ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Chip
                      icon={<LinkIcon />}
                      label="연동됨"
                      color="success"
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {user.wedms_username}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={disconnectWedmsMutation.isPending ? <CircularProgress size={16} /> : <LinkOffIcon />}
                    onClick={handleDisconnectWedms}
                    disabled={disconnectWedmsMutation.isPending || processingPending}
                  >
                    연동 해제
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Chip
                    label="연동 안됨"
                    color="default"
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={handleOpenWedmsDialog}
                    >
                      WeDMS 연동
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* WeDMS 연동 다이얼로그 */}
      <Dialog open={wedmsDialogOpen} onClose={() => setWedmsDialogOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handleWedmsSubmit(onWedmsSubmit)}>
          <DialogTitle>WeDMS 연동</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              WeDMS 계정 정보를 입력하여 연동합니다.
            </Typography>

            {wedmsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {wedmsError}
              </Alert>
            )}

            <Controller
              name="wedms_username"
              control={wedmsControl}
              rules={{ required: 'WeDMS 사용자명을 입력하세요.' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="WeDMS 사용자명"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{ mb: 2 }}
                  autoFocus
                />
              )}
            />

            <Controller
              name="wedms_password"
              control={wedmsControl}
              rules={{ required: 'WeDMS 비밀번호를 입력하세요.' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="password"
                  label="WeDMS 비밀번호"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWedmsDialogOpen(false)}>취소</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={connectWedmsMutation.isPending}
              startIcon={connectWedmsMutation.isPending ? <CircularProgress size={16} /> : <LinkIcon />}
            >
              {connectWedmsMutation.isPending ? '연동 중...' : '연동'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;
